/**
 * THE ONE TRANSPORT. No component, hook or feature calls fetch() directly.
 *
 * Every request gets:
 *   - the base URL (api/config)
 *   - X-Auth-Token — the session token, when there is one (api/token). Rachel's
 *     decision 18-09-2026: a header token, not a cookie. `credentials` is
 *     therefore "omit": nothing here rides on a cookie, and the API is served
 *     from its own origin.
 *   - a timeout
 *
 * The client sends no `X-Correlation-Id` and no `Idempotency-Key`. The backend
 * does not use either (`DECISIONS.md`, 24-09-2026). A mutation is still never
 * retried automatically.
 * and every failure is thrown as an AppError (api/problems).
 *
 * Every call goes through `createApiClient<paths>()`: openapi-fetch over this
 * same transport, typed from the module's document in `src/api/generated/`.
 */
import createClient, { type Middleware } from "openapi-fetch";
import { apiBaseUrl } from "./config";
import { AppError, errorFromResponse } from "./problems";
import { authHeaderValue } from "./token";

export const DEFAULT_TIMEOUT_MS = 20_000;

/** Adds the platform headers to a request's headers. */
export function applyStandardHeaders(headers: Headers): void {
  if (!headers.has("Accept")) headers.set("Accept", "application/json, application/problem+json");
  const token = authHeaderValue();
  if (token !== null && !headers.has("X-Auth-Token")) headers.set("X-Auth-Token", token);
}

/** fetch with a timeout, turning "no answer" into a typed AppError. */
async function send(request: Request, timeoutMs: number): Promise<Response> {
  const timeout = AbortSignal.timeout(timeoutMs);
  const signal = AbortSignal.any([request.signal, timeout]);
  try {
    return await fetch(request, { signal });
  } catch (cause) {
    if (timeout.aborted) throw new AppError("timeout", { cause });
    if (request.signal.aborted) throw cause; // the caller cancelled: not an error to report
    throw new AppError("network", { cause });
  }
}

const standardHeaders: Middleware = {
  onRequest({ request }) {
    applyStandardHeaders(request.headers);
    return request;
  },
  async onResponse({ request, response }) {
    if (!response.ok) throw await errorFromResponse(response);
    return requireJson(request, response);
  },
};

function pathOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

/**
 * A success the generated client is about to parse. A body that is not JSON —
 * a proxy's HTML, a truncated payload — is a contract error carrying the status,
 * not a SyntaxError from the parser. An empty body is left for the caller,
 * which is where "this payload does not match" is decided.
 */
async function requireJson(request: Request, response: Response): Promise<Response> {
  if (response.status === 204 || request.method === "HEAD") return response;
  const length = response.headers.get("content-length");
  if (length === "0") return response;
  const raw = await response.text();
  if (raw.trim() === "") {
    return new Response(null, { status: response.status, headers: response.headers });
  }
  try {
    JSON.parse(raw);
  } catch (cause) {
    throw new AppError("contract", {
      status: response.status,
      cause,
      message: `${pathOf(request.url)} did not return JSON`,
    });
  }
  // The body was consumed to check it. Hand the same bytes on, without the
  // encoding headers that described the original stream.
  const headers = new Headers(response.headers);
  headers.delete("content-encoding");
  headers.delete("content-length");
  return new Response(raw, { status: response.status, statusText: response.statusText, headers });
}

/**
 * The typed client for a module's generated `paths` (src/api/generated/<module>.ts).
 * Failures throw AppError, so a query or mutation sees one error shape whichever
 * path it took.
 */
export function createApiClient<Paths extends object>(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const client = createClient<Paths>({
    baseUrl: apiBaseUrl(),
    credentials: "omit",
    fetch: (request: Request) => send(request, timeoutMs),
  });
  client.use(standardHeaders);
  return client;
}
